/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useLocation } from 'react-router';
import type { TileSurface } from '@/lib/brand/overlay-model';

/**
 * The two `TileSurface` values a product grid can be rendered on. Narrower than
 * `TileSurface` on purpose: a grid is never a homepage rail or a recommendation
 * carousel, and typing the prop as the full union would invite a caller to say so.
 */
export type ProductGridSurface = Extract<TileSurface, 'search-grid' | 'plp-grid'>;

/**
 * The PLP's own path segment. `routes/_app.category.$categoryId` mounts at
 * `/category/:categoryId`, and every route additionally sits under a `/:siteId`
 * prefix (`config.server.ts` → `app.url.prefix`), so the live pathname is
 * `/NeimanMarcus/category/womens-clothing` — verified on localhost:5173,
 * 2026-08-20, alongside `/NeimanMarcus/search` for the other route.
 *
 * MATCHED ON THE SEGMENT, NOT ON A SUBSTRING OF THE WHOLE PATH, and the
 * discriminator is the presence of `category` rather than the absence of
 * `search`. That ordering matters: a category slugged `search`
 * (`/NeimanMarcus/category/search`) still reads as the PLP, because the slug is
 * never consulted.
 *
 * WHY NOT `useMatches`, WHICH WOULD BE PRECISE. It requires a DATA router and
 * throws `useMatches must be used within a data router` under a plain
 * `<MemoryRouter>`. `ProductGrid` has only ever needed a plain router — its
 * other router call is `useSearchParams`, which works under both — so reaching
 * for `useMatches` here would quietly escalate this component's contract and
 * crash every caller outside a data router (stories, Page Designer previews, the
 * existing tests). Measured, not predicted: it took out 26 tests in
 * `grid.test.tsx` on the first run. Precision is not worth a new crash mode.
 */
const PLP_PATH_SEGMENT = 'category';

/**
 * Which surface this grid is rendering on, derived from the matched route.
 *
 * WHY THIS IS DERIVED AND NOT SIMPLY PASSED. It *should* be passed — a route
 * declaring its own surface is the same idiom the sticky bar already uses
 * (`surface="search"` / `surface="plp"` at `_app.search.tsx` and
 * `_app.category.$categoryId.tsx`). `ProductGrid` accepts exactly that prop and
 * it wins whenever it is given. This hook is the fallback for the two callers
 * that do not pass it yet, and it exists so that the defect it fixes — BG's PLP
 * painting a `View Similar` bar live does not have — is fixed NOW rather than
 * parked behind an edit to two route files.
 *
 * Note this is NOT a brand branch. Brand never appears here; the grid genuinely
 * renders on two different surfaces and has to know which one it is standing on.
 * The per-brand answer is looked up from that surface in
 * `lib/brand/overlay-model.ts`, which is the layer that owns brand divergence.
 *
 * DEFAULTS TO `search-grid`, matching `DEFAULT_TILE_SURFACE`. Anything that is
 * neither route — a story, a test harness, a Page Designer preview — keeps the
 * behaviour it had before search and PLP were separable.
 */
export function useProductGridSurface(): ProductGridSurface {
    const { pathname } = useLocation();
    const segments = pathname.split('/').filter(Boolean);
    // The segment must have something AFTER it — `/category/:categoryId` always
    // carries a slug, so a bare `/category` is not the PLP.
    const at = segments.indexOf(PLP_PATH_SEGMENT);
    return at >= 0 && at < segments.length - 1 ? 'plp-grid' : 'search-grid';
}
